/**
 * Routes, and the one thing that has to happen before any of them render.
 *
 * A remembered session is checked against the server on startup. Until that
 * answers, RequireAuth holds rather than redirecting — otherwise every reload
 * would bounce a valid session to the login screen.
 */
import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import { useAuth } from './stores/auth.js';
import { RequireAuth } from './routes/RequireAuth.jsx';
import LoginPage from './features/auth/LoginPage.jsx';
import PosPage from './features/pos/PosPage.jsx';
import PastOrdersPage from './features/pos/PastOrdersPage.jsx';
import KitchenPage from './features/kitchen/KitchenPage.jsx';
import OrdersPage from './features/orders/OrdersPage.jsx';
import DashboardPage from './features/reports/DashboardPage.jsx';
import MenuPage from './features/menu/MenuPage.jsx';
import StockPage from './features/inventory/StockPage.jsx';
import StorePage from './features/store/StorePage.jsx';
import PurchasingPage from './features/purchasing/PurchasingPage.jsx';
import DispatchPage from './features/delivery/DispatchPage.jsx';
import ExpensesPage from './features/accounts/ExpensesPage.jsx';
import StaffPage from './features/admin/StaffPage.jsx';
import AccountHistoryPage from './features/admin/AccountHistoryPage.jsx';
import FoodpandaPage from './features/foodpanda/FoodpandaPage.jsx';
import ApprovalsPage from './features/approvals/ApprovalsPage.jsx';
import CustomersPage from './features/crm/CustomersPage.jsx';
import ProfitLossPage from './features/reports/ProfitLossPage.jsx';
import BalanceSheetPage from './features/reports/BalanceSheetPage.jsx';
import TaxReportPage from './features/reports/TaxReportPage.jsx';
import PurchaseReportPage from './features/reports/PurchaseReportPage.jsx';
import ExpenseReportPage from './features/reports/ExpenseReportPage.jsx';
import LedgerPage from './features/reports/LedgerPage.jsx';
import BreakdownPage from './features/reports/BreakdownPage.jsx';
import AdjustmentsPage from './features/reports/AdjustmentsPage.jsx';
import CustomerReportPage from './features/reports/CustomersPage.jsx';
import TaxSettingsPage from './features/settings/TaxSettingsPage.jsx';
import SettingsPage from './features/settings/SettingsPage.jsx';
import ReconciliationPage from './features/banking/ReconciliationPage.jsx';
import VouchersPage from './features/vouchers/VouchersPage.jsx';
import BranchesPage from './features/branches/BranchesPage.jsx';
import FloorPage from './features/floor/FloorPage.jsx';
import { BackOfficeShell } from './components/layout/BackOfficeShell.jsx';
import { TillShell } from './components/layout/TillShell.jsx';
import { KitchenShell } from './components/layout/KitchenShell.jsx';
import { homeFor } from './components/layout/nav.js';

/**
 * The first screen after signing in.
 *
 * Everybody used to be sent to the till. The kitchen account has no permission
 * for it, so signing in on the kitchen screen produced "You do not have access
 * to this" — a true sentence and a poor welcome, for a screen that was never
 * meant to go there. Each account now opens on the first place its own
 * permissions allow.
 */
function Home() {
  const can = useAuth((s) => s.can);
  const roles = useAuth((s) => s.user?.roles ?? []);
  const where = homeFor(can, roles);

  if (where) return <Navigate to={where} replace />;

  return (
    <main className="flex min-h-dvh items-center justify-center p-6 text-center">
      <div>
        <h1 className="font-semibold text-xl text-ink">Nothing is set up for you yet</h1>
        <p className="mx-auto mt-2 max-w-sm text-sm text-ink-2">
          Your account has no screens on it. Ask a manager to give it a job, then sign out and in
          again.
        </p>
      </div>
    </main>
  );
}

export default function App() {
  const refreshProfile = useAuth((s) => s.refreshProfile);

  useEffect(() => {
    refreshProfile();
  }, [refreshProfile]);

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      {/*
        Three frames, because three different jobs.

        The till is one screen a cashier stands at for hours: no navigation
        beside the Charge button, and the width goes to the products. The
        kitchen is a wall display nobody navigates from. Everything else is
        back-office work at a desk, where a sidebar holds seven destinations
        now and more after Phase 4.
      */}
      <Route
        path="/pos"
        element={
          <RequireAuth permission="pos.create.pos">
            <TillShell />
          </RequireAuth>
        }
      >
        <Route index element={<PosPage />} />
      </Route>

      <Route
        path="/kitchen"
        element={
          <RequireAuth permission="kitchen.read.kitchen">
            <KitchenShell />
          </RequireAuth>
        }
      >
        <Route index element={<KitchenPage />} />
      </Route>

      <Route
        path="/"
        element={
          <RequireAuth>
            <BackOfficeShell />
          </RequireAuth>
        }
      >
        <Route index element={<Home />} />
        <Route path="orders" element={<OrdersPage />} />
        <Route path="delivery" element={<DispatchPage />} />
        <Route path="menu" element={<MenuPage />} />
        <Route path="inventory" element={<StockPage />} />
        <Route path="store" element={<StorePage />} />
        <Route path="purchasing" element={<PurchasingPage />} />
        <Route path="expenses" element={<ExpensesPage />} />
        <Route path="reports" element={<DashboardPage />} />
        <Route path="customers" element={<CustomersPage />} />
        <Route path="staff" element={<StaffPage />} />
        <Route path="staff/:id/account" element={<AccountHistoryPage />} />
        <Route path="foodpanda" element={<FoodpandaPage />} />
        <Route path="approvals" element={<ApprovalsPage />} />
        <Route path="profit-loss" element={<ProfitLossPage />} />
        <Route path="balance-sheet" element={<BalanceSheetPage />} />
        <Route path="tax" element={<TaxReportPage />} />
        <Route path="purchase-report" element={<PurchaseReportPage />} />
        <Route path="expense-report" element={<ExpenseReportPage />} />
        <Route path="ledger" element={<LedgerPage />} />
        <Route path="sales-breakdown" element={<BreakdownPage />} />
        <Route path="adjustments" element={<AdjustmentsPage />} />
        <Route path="customer-report" element={<CustomerReportPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="settings/tax" element={<TaxSettingsPage />} />
        <Route path="banking" element={<ReconciliationPage />} />
        <Route path="vouchers" element={<VouchersPage />} />
        <Route path="past-orders" element={<PastOrdersPage />} />
        <Route path="branches" element={<BranchesPage />} />
        <Route path="floor" element={<FloorPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
